import * as React from 'react'
import { useMemo, useState } from 'react'
import { Button, Menu, MenuItem, MenuList, MenuPopover, MenuTrigger, mergeClasses, Text, Tooltip } from '@fluentui/react-components'
import PersonAvatar from '../../components/PersonAvatar'
import { Link } from 'react-router-dom'
import useViewport, { Viewport } from '../../hooks/useViewport'
import { useOverviewStyles } from '../BaseOverview/BaseOverview'
import { DateFormats, formatIsoDate } from '../../helpers/dateHelper'
import {
    AttachRegular,
    bundleIcon,
    CheckmarkFilled,
    CheckmarkRegular,
    FlagFilled,
    FlagRegular,
    FlowchartFilled,
    ImportantFilled,
    ImportantRegular,
    NewFilled,
    NewRegular,
    SettingsFilled,
    SettingsRegular,
    ShiftsActivityFilled,
    ShiftsActivityRegular,
} from '@fluentui/react-icons'
import useTaskStatuses from '../../hooks/useTaskStatuses'
import { BaseTaskOverviewLineItemProps, useItemStyles } from './BaseTaskOverview'
import { TaskStatus } from '../../context/GlobalContext'
import DocumentLink from '../../components/Document/DocumentLink'
import { useTranslation } from 'react-i18next'
import useAxios from '../../hooks/useAxios'
import useTasks from '../../hooks/useTasks'
import TimeText from '../../components/Text/Time/TimeText'
import { ViewType } from '../BaseOverview/BaseOverviewFilter'
import EditableFormattedDateRange from '../../components/Date/EditableFormattedDateRange'
import useUsers from '../../hooks/useUsers'
import { ActiveUserStatuses } from '../../config/userConfig'

const SettingsIcon = bundleIcon(SettingsFilled, SettingsRegular)
const NewIcon = bundleIcon(NewFilled, NewRegular)
const CheckmarkIcon = bundleIcon(CheckmarkFilled, CheckmarkRegular)
const ShiftsActivityIcon = bundleIcon(ShiftsActivityFilled, ShiftsActivityRegular)

export function getStatusIcon(status: TaskStatus) {
    if (status.isStartStatus) {
        return <NewIcon />
    } else if (status.isEndStatus) {
        return <CheckmarkIcon />
    } else {
        return <ShiftsActivityIcon />
    }
}

export function getFlagIcon(props: BaseTaskOverviewLineItemProps, itemStyles: Record<'filterTagToday' | 'filterTagWeek', string>) {
    if (props.item.today && props.item.week) {
        if (props.filterValues.today) {
            return <FlagFilled className={itemStyles.filterTagToday} />
        } else if (props.filterValues.week) {
            return <FlagFilled className={itemStyles.filterTagWeek} />
        }
    }
    if (props.item.today) {
        return <FlagFilled className={itemStyles.filterTagToday} />
    } else if (props.item.week) {
        return <FlagFilled className={itemStyles.filterTagWeek} />
    }

    return <SettingsIcon />
}

function TaskListItemAssignee(props: BaseTaskOverviewLineItemProps) {
    const axios = useAxios()
    const itemStyles = useItemStyles()
    const { t: translate } = useTranslation()
    const { users } = useUsers()

    return (
        <Menu hasIcons>
            <MenuTrigger disableButtonEnhancement>
                <Tooltip relationship={'label'} content={`${translate('task.filter.assignee')}: ${props.item.assignee?.fullName}`} withArrow>
                    <div className={itemStyles.listTaskItemMenuButton}>
                        <PersonAvatar tooltip={false} name={props.item.assignee?.fullName} color={'colorful'} size={24} />
                    </div>
                </Tooltip>
            </MenuTrigger>
            <MenuPopover>
                <MenuList>
                    {users
                        .filter(entity => props.item.assignee?.id === entity.id || ActiveUserStatuses.includes(entity.status))
                        .map((user, index) => (
                            <MenuItem
                                key={index}
                                onClick={() => {
                                    axios
                                        .post(`/task/${props.item.id}/assignee`, user.id, {
                                            headers: { 'Content-Type': 'text/plain' },
                                        })
                                        .then(() =>
                                            props.onUpdateItem(props.item, [
                                                props.columnIndex,
                                                props.filterValues.statuses.indexOf(props.item.status),
                                            ]),
                                        )
                                }}
                            >
                                {user.fullName}
                            </MenuItem>
                        ))}
                </MenuList>
            </MenuPopover>
        </Menu>
    )
}

function TaskListItemStatus(props: BaseTaskOverviewLineItemProps) {
    const axios = useAxios()
    const { t: translate } = useTranslation()
    const { taskStatuses, getStatus, getStatusName } = useTaskStatuses()

    const userStatusIcon = getStatusIcon(getStatus(props.item.status))

    return (
        <Menu hasIcons>
            <MenuTrigger disableButtonEnhancement>
                <Tooltip relationship={'label'} content={`${translate('task.filter.status.label')}: ${getStatusName(props.item.status)}`} withArrow>
                    <Button appearance="subtle" size={'small'} icon={userStatusIcon} />
                </Tooltip>
            </MenuTrigger>
            <MenuPopover>
                <MenuList>
                    {taskStatuses.map((status, index) => (
                        <MenuItem
                            key={index}
                            icon={getStatusIcon(status)}
                            onClick={() => {
                                axios
                                    .post(`/task/${props.item.id}/status`, status.alpha, {
                                        headers: { 'Content-Type': 'text/plain' },
                                    })
                                    .then(() =>
                                        props.onUpdateItem(props.item, [props.columnIndex, props.filterValues.statuses.indexOf(status.alpha)]),
                                    )
                            }}
                        >
                            {getStatusName(status.alpha)}
                        </MenuItem>
                    ))}
                </MenuList>
            </MenuPopover>
        </Menu>
    )
}

function TaskListItemActions(props: BaseTaskOverviewLineItemProps) {
    const axios = useAxios()
    const itemStyles = useItemStyles()
    const { t: translate } = useTranslation()

    const [open, setOpen] = useState<boolean>(false)

    return (
        <Menu hasIcons open={open} onOpenChange={(_e, data) => setOpen(data.open)}>
            <MenuTrigger disableButtonEnhancement>
                <Button
                    size={'small'}
                    appearance={'subtle'}
                    icon={getFlagIcon(props, itemStyles)}
                    aria-label={
                        props.item.today ? 'flag-today-active' : props.item.week ? 'flag-week-active' : open ? 'flag-action-active' : 'flag-action'
                    }
                />
            </MenuTrigger>
            <MenuPopover>
                <MenuList>
                    <MenuItem
                        icon={props.item.highlight ? <ImportantFilled className={itemStyles.filterTagHighlight} /> : <ImportantRegular />}
                        onClick={() => {
                            axios.post(`/task/${props.item.id}/highlight`).then(() => props.onUpdateItem(props.item, [props.columnIndex]))
                        }}
                    >
                        {translate(props.item.highlight ? 'task.nothighlight' : 'task.highlight')}
                    </MenuItem>
                    <MenuItem
                        icon={props.item.week ? <FlagFilled className={itemStyles.filterTagWeek} /> : <FlagRegular />}
                        onClick={() => {
                            axios.post(`/task/${props.item.id}/week`).then(() => props.onUpdateItem(props.item, [props.columnIndex]))
                        }}
                    >
                        {translate(props.item.week ? 'task.notweek' : 'task.week')}
                    </MenuItem>
                    <MenuItem
                        icon={props.item.today ? <FlagFilled className={itemStyles.filterTagToday} /> : <FlagRegular />}
                        onClick={() => {
                            axios.post(`/task/${props.item.id}/today`).then(() => props.onUpdateItem(props.item, [props.columnIndex]))
                        }}
                    >
                        {translate(props.item.today ? 'task.nottoday' : 'task.today')}
                    </MenuItem>
                </MenuList>
            </MenuPopover>
        </Menu>
    )
}

export function TaskListItemAttachments(props: BaseTaskOverviewLineItemProps) {
    const [showMenu, setShowMenu] = useState<boolean>(false)

    if (props.item.attachmentIds?.length === 0) {
        return <div></div>
    }

    return (
        <Menu positioning={{ autoSize: true }} open={showMenu} onOpenChange={(_, { open }) => setShowMenu(open)}>
            <MenuTrigger disableButtonEnhancement>
                <Button icon={<AttachRegular />} appearance="transparent" size={'small'} onClick={() => setShowMenu(!showMenu)} />
            </MenuTrigger>

            <MenuPopover>
                <MenuList>{showMenu && props.item.attachmentIds?.map((attachment, index) => <DocumentLink key={index} id={attachment} />)}</MenuList>
            </MenuPopover>
        </Menu>
    )
}

export function TaskListItemWorkflow(props: BaseTaskOverviewLineItemProps) {
    const itemStyles = useItemStyles()

    return (
        <>
            {props.item.workflowStepName && (
                <Tooltip relationship={'label'} content={props.item.workflowStepName} withArrow>
                    <Button appearance={'transparent'} size={'small'} icon={<FlowchartFilled />} className={itemStyles.workflowColor} />
                </Tooltip>
            )}
            {!props.item.workflowStepName && <div></div>}
        </>
    )
}

export function TaskListItemFile(props: BaseTaskOverviewLineItemProps) {
    const overviewStyles = useOverviewStyles()

    return (
        <>
            {!props.fileId ? (
                <Tooltip relationship={'label'} content={props.item.file.name} withArrow>
                    <Link key={props.item.id} to={`/file/${props.item.file.id}`} className={overviewStyles.alignEnd}>
                        <Text size={200}>{props.item.file.reference}</Text>
                    </Link>
                </Tooltip>
            ) : (
                <div></div>
            )}
        </>
    )
}

function TaskListItemExpectedTime(props: BaseTaskOverviewLineItemProps) {
    const { expectedTime } = useTasks()

    return expectedTime ? (
        <div>
            <TimeText value={props.item.duration || '0:00'} />
        </div>
    ) : (
        <div></div>
    )
}

function TaskListItemCategory(props: BaseTaskOverviewLineItemProps) {
    const { categories, getCategoryById } = useTasks()
    const overviewStyles = useOverviewStyles()

    return categories?.length > 0 && props.item.category ? (
        <Tooltip relationship={'label'} content={getCategoryById(props.item.category)?.description} withArrow>
            <Text className={overviewStyles.alignEnd}>{getCategoryById(props.item.category)?.name}</Text>
        </Tooltip>
    ) : (
        <div></div>
    )
}

export default function TaskListItem(props: BaseTaskOverviewLineItemProps) {
    const axios = useAxios()
    const overviewStyles = useOverviewStyles()
    const itemStyles = useItemStyles()
    const { viewport, isViewport } = useViewport()

    const listItemSmall = useMemo<boolean>(() => props.viewType === ViewType.KanbanView || isViewport('<', Viewport.OutlookNewWide), [viewport])

    if (listItemSmall) {
        return (
            <>
                <div className={itemStyles.listTaskItemSmallRow1}>
                    <TaskListItemAssignee {...props} />
                    <Link key={props.item.id} to={`${props.baseUrl}/${props.item.id}/edit`}>
                        <Tooltip relationship={'label'} content={props.item.description} withArrow>
                            <Text className={itemStyles.listTaskItemOverflow}>{props.item.title}</Text>
                        </Tooltip>
                    </Link>
                </div>
                <div className={mergeClasses(itemStyles.listTaskItemSmallRow2, overviewStyles.alignItemsStart)}>
                    <TaskListItemExpectedTime {...props} />
                    <TaskListItemAttachments {...props} />
                    <TaskListItemCategory {...props} />
                </div>
                <div className={itemStyles.listTaskItemSmallRow3}>
                    <EditableFormattedDateRange
                        fromDate={props.item.start}
                        toDate={props.item.due}
                        date={props.item.due}
                        format={DateFormats.Full}
                        setDate={date =>
                            axios
                                .post(`/task/${props.item.id}/date`, formatIsoDate(date), { headers: { 'Content-Type': 'text/plain' } })
                                .then(() => props.onUpdateItem(props.item, [props.columnIndex]))
                        }
                    />
                    <TaskListItemStatus {...props} />
                    <TaskListItemActions {...props} />
                    <TaskListItemWorkflow {...props} />
                    <TaskListItemFile {...props} />
                    <PersonAvatar name={props.item.assigner.fullName} color={'colorful'} size={24} />
                </div>
            </>
        )
    }

    return (
        <>
            <TaskListItemAssignee {...props} />
            <TaskListItemStatus {...props} />
            <EditableFormattedDateRange
                fromDate={props.item.start}
                toDate={props.item.due}
                date={props.item.due}
                format={DateFormats.Full}
                setDate={date =>
                    axios
                        .post(`/task/${props.item.id}/date`, formatIsoDate(date), { headers: { 'Content-Type': 'text/plain' } })
                        .then(() => props.onUpdateItem(props.item, [props.columnIndex]))
                }
            />

            <div className={itemStyles.listTaskItemTitle}>
                <TaskListItemExpectedTime {...props} />
                <Link key={props.item.id} to={`${props.baseUrl}/${props.item.id}/edit`}>
                    <Tooltip relationship={'label'} content={props.item.description} withArrow>
                        <Text className={itemStyles.listTaskItemOverflow}>{props.item.title}</Text>
                    </Tooltip>
                </Link>
                <TaskListItemCategory {...props} />
            </div>
            <TaskListItemActions {...props} />
            <TaskListItemAttachments {...props} />
            <TaskListItemWorkflow {...props} />
            <TaskListItemFile {...props} />
            <PersonAvatar name={props.item.assigner?.fullName} color={'colorful'} size={24} />
        </>
    )
}
